import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { Modal } from '../../../shared/components/Modal';
import { Select } from '../../../shared/components/Select';
import { TextArea } from '../../../shared/components/TextArea';
import { Toggle } from '../../../shared/components/Toggle';
import { useCreateOutreachTemplate, useUpdateOutreachTemplate } from '../hooks/useOutreach';
import {
  CHANNEL_LABELS,
  ENGAGEMENT_MODEL_LABELS,
  OutreachChannel,
  OutreachEngagementModel,
  type OutreachTemplate,
} from '../types';

interface Props {
  /** Absent = create mode. */
  template?: OutreachTemplate;
  /**
   * Create-mode prefill — the "save as template" path from a contact's draft composer, which
   * also marks the template as having started life as an AI draft.
   */
  prefill?: {
    engagementModel: OutreachEngagementModel;
    channel: OutreachChannel;
    body: string;
    originAiDrafted: boolean;
  };
  onClose: () => void;
}

const optionsFrom = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, title]) => ({ value, title }));

/**
 * Create or edit an outreach message template.
 *
 * Engagement model and channel are only chosen at create time — UpdateOutreachTemplateDto
 * accepts name/body/isActive only, so an edit cannot move a template between models. Name and
 * body are @MinLength(2) server-side; the submit button mirrors that.
 */
export function TemplateFormModal({ template, prefill, onClose }: Props) {
  const isEdit = !!template;
  const create = useCreateOutreachTemplate();
  const update = useUpdateOutreachTemplate();

  const [engagementModel, setEngagementModel] = useState<OutreachEngagementModel>(
    prefill?.engagementModel ?? OutreachEngagementModel.WORKING_PREVIEW,
  );
  const [channel, setChannel] = useState<OutreachChannel>(
    prefill?.channel ?? OutreachChannel.WHATSAPP,
  );
  const [name, setName] = useState(template?.name ?? '');
  const [body, setBody] = useState(template?.body ?? prefill?.body ?? '');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);

  const isPending = create.isPending || update.isPending;
  const valid = name.trim().length >= 2 && body.trim().length >= 2;

  const submit = async () => {
    if (!valid) return;
    if (isEdit) {
      await update.mutateAsync({
        id: template.id,
        payload: { name: name.trim(), body: body.trim(), isActive },
      });
    } else {
      await create.mutateAsync({
        engagementModel,
        channel,
        name: name.trim(),
        body: body.trim(),
        ...(prefill?.originAiDrafted ? { originAiDrafted: true } : {}),
      });
    }
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto themed-scrollbar">
        <h2 className="text-[18px] font-semibold text-foreground">
          {isEdit ? 'Edit template' : 'New template'}
        </h2>

        {isEdit ? (
          <p className="text-sm text-muted-foreground">
            {ENGAGEMENT_MODEL_LABELS[template.engagementModel]} ·{' '}
            {CHANNEL_LABELS[template.channel]}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Engagement model"
              options={optionsFrom(ENGAGEMENT_MODEL_LABELS)}
              value={engagementModel}
              onChange={(value) => setEngagementModel(value as OutreachEngagementModel)}
            />
            <Select
              label="Channel"
              options={optionsFrom(CHANNEL_LABELS)}
              value={channel}
              onChange={(value) => setChannel(value as OutreachChannel)}
            />
          </div>
        )}

        <Input
          className="min-h-10 lg:min-h-0"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. First touch — fashion, WhatsApp"
        />
        <TextArea
          label="Message"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          placeholder="The message staff start from…"
        />

        {isEdit && (
          <Toggle
            label="Active — offered when composing a message"
            checked={isActive}
            onChange={setIsActive}
          />
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" size="sm" className="h-[40px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-[40px]"
            onClick={submit}
            disabled={!valid || isPending}
            loading={isPending}
          >
            {isEdit ? 'Save template' : 'Create template'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
